import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/kfmApi";
import { withUtm } from "@/lib/utm";
import { X, Trash2, PenLine, Camera, Download, Copy, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Highlight a snippet of study text and pin a small handwritten note to it — the note
// stays hidden in the margin until the reader hovers (quick peek) or clicks (edit/delete).
export const HL_COLORS = {
  yellow: { mark: "#FDE68A", strong: "#F5CB4C", ring: "#B45309" },
  green: { mark: "#BBF7D0", strong: "#86EFAC", ring: "#15803D" },
  pink: { mark: "#FBCFE8", strong: "#F9A8D4", ring: "#BE185D" },
  blue: { mark: "#BFDBFE", strong: "#93C5FD", ring: "#1D4ED8" },
};

// "Share as image" (camera icon). Kill switch: flipping this to false removes the button
// with no other change, and no deploy of anything else.
const IMAGE_CAPTURE_ENABLED = true;

// Ceiling on a single selection. Generous on purpose: the card renderer autofits, so
// long passages shrink to fit rather than being trimmed.
const MAX_SELECTION = 2000;

// Page furniture that sits inside the prose but is not the reader's words: Strong's
// citation chips (visible text is a bare code like "G509") and superscript verse
// numbers (which would otherwise glue onto the next word, "3knowing"). Reading
// window.getSelection().toString() would drag all of it onto the card.
const NOT_THE_READERS_WORDS = [
  '[data-testid^="inline-strongs-"]',
  '[data-testid^="concordance-open-"]',
  '[data-testid="verse-number"]',
].join(", ");

// Does the selection touch this node at all? Used instead of "where did it start",
// because a drag begun in a paragraph's leading padding, or in a heading, anchors
// startContainer on the parent <p>/<h2> rather than inside our span.
function rangeTouches(range, node) {
  try {
    if (typeof range.intersectsNode === "function") return range.intersectsNode(node);
  } catch (e) { /* fall through to the boundary comparison */ }
  const probe = document.createRange();
  probe.selectNodeContents(node);
  return (
    range.compareBoundaryPoints(Range.END_TO_START, probe) < 0 &&
    range.compareBoundaryPoints(Range.START_TO_END, probe) > 0
  );
}

function selectionTextForCard(range) {
  const frag = range.cloneContents();
  frag.querySelectorAll(NOT_THE_READERS_WORDS).forEach((n) => n.remove());
  return frag.textContent.replace(/\s+/g, " ").trim();
}

// Turn a Range selection inside `container` into plain-text character offsets, so the
// highlight survives re-renders (it's anchored to text position + the text itself).
function rangeToOffsets(container, range) {
  const pre = document.createRange();
  pre.selectNodeContents(container);
  pre.setEnd(range.startContainer, range.startOffset);
  const start = pre.toString().length;
  return [start, start + range.toString().length];
}

// Only one selection toolbar / note editor should ever be open at a time across the whole
// page (a study has many independent HighlightableText instances, one per block).
const TOOLBAR_EVENT = "ow-hl-toolbar-open";
const NOTE_EVENT = "ow-hl-note-open";

function resolveHighlights(text, highlights) {
  const resolved = [];
  for (const h of highlights) {
    let start = h.offset;
    if (text.slice(start, start + h.text.length) !== h.text) {
      const idx = text.indexOf(h.text);
      if (idx === -1) continue; // study text changed underneath — drop this orphaned highlight
      start = idx;
    }
    resolved.push({ ...h, start, end: start + h.text.length });
  }
  resolved.sort((a, b) => a.start - b.start);
  const out = [];
  let lastEnd = -1;
  for (const h of resolved) {
    if (h.start < lastEnd) continue; // no overlapping highlights
    out.push(h);
    lastEnd = h.end;
  }
  return out;
}

export default function HighlightableText({
  sessionId,
  block,
  text,
  highlights = [],
  onChange,
  editable = true,
  renderText,
  className = "",
  tag = "span",
  sharePath,
  cardKind = "scripture",
  reference = "",
  translation = "",
}) {
  const containerRef = useRef(null);
  const toolbarRef = useRef(null);
  const toolbarLive = useRef(null); // mirrors `toolbar` state for use inside stable listeners
  const instanceId = useRef(`${block}-${Math.random().toString(36).slice(2)}`).current;
  const [toolbar, setToolbar] = useState(null); // { x, y, rect, start, end, selectedText }
  const [activeId, setActiveId] = useState(null);
  const [activeRect, setActiveRect] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [hoverRect, setHoverRect] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [capture, setCapture] = useState(null); // { blobUrl, sharing }
  const noteInputRef = useRef(null);
  toolbarLive.current = toolbar;

  const plain = text || "";
  const resolved = resolveHighlights(plain, highlights.filter((h) => h.block === block));
  const activeHighlight = resolved.find((h) => h.id === activeId) || null;

  useEffect(() => {
    if (!editable) return;
    const onUp = (e) => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const container = containerRef.current;
      // Exactly one block owns a drag: the first one it touches, in document order.
      // Scripture renders a block per verse and prose a block per paragraph, so a drag
      // legitimately spans several; and it often begins outside any of them (a heading,
      // or the padding before a paragraph's first word), which is why touching beats
      // "starts inside".
      if (!container || !rangeTouches(range, container)) return;
      const blocks = document.querySelectorAll('[data-testid^="highlightable-"]');
      if ([...blocks].find((el) => rangeTouches(range, el)) !== container) return;
      if (!sel.toString().trim() || sel.toString().length > MAX_SELECTION) return;

      // The card gets the whole selection, chips stripped.
      const cardText = selectionTextForCard(range);
      if (!cardText) return;

      // A highlight belongs to one block, so clip the range to this container before
      // deriving its offsets and stored text.
      const clipped = range.cloneRange();
      if (!container.contains(range.startContainer)) clipped.setStart(container, 0);
      if (!container.contains(range.endContainer)) {
        clipped.setEnd(container, container.childNodes.length);
      }
      const [start, end] = rangeToOffsets(container, clipped);
      const rect = range.getBoundingClientRect();
      if (!rect.width && !rect.height) return;
      setToolbar({
        x: rect.left + rect.width / 2, y: Math.max(rect.top, 64),
        rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
        start, end, selectedText: clipped.toString(), cardText,
      });
      window.dispatchEvent(new CustomEvent(TOOLBAR_EVENT, { detail: instanceId }));
    };
    // A click outside our own toolbar (and not the click that opens it) closes it.
    const onMouseDownOutside = (e) => {
      if (toolbarLive.current && toolbarRef.current && !toolbarRef.current.contains(e.target)) {
        setToolbar(null);
      }
    };
    // A new selection/toolbar opened in a different block invalidates ours.
    const onOtherToolbar = (e) => { if (e.detail !== instanceId) setToolbar(null); };
    const onOtherNote = (e) => { if (e.detail !== instanceId) setActiveId(null); };
    const onKeyDown = (e) => { if (e.key === "Escape") { setToolbar(null); setActiveId(null); } };
    const onScroll = () => setToolbar(null);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("mousedown", onMouseDownOutside);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener(TOOLBAR_EVENT, onOtherToolbar);
    window.addEventListener(NOTE_EVENT, onOtherNote);
    return () => {
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("mousedown", onMouseDownOutside);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener(TOOLBAR_EVENT, onOtherToolbar);
      window.removeEventListener(NOTE_EVENT, onOtherNote);
    };
  }, [editable, instanceId]);

  useEffect(() => {
    if (activeId && noteInputRef.current) noteInputRef.current.focus();
  }, [activeId]);

  const dismissToolbar = () => {
    setToolbar(null);
    window.getSelection()?.removeAllRanges();
  };

  const createHighlight = async (color) => {
    if (!toolbar) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/study/sessions/${sessionId}/highlights`, {
        block,
        text: toolbar.selectedText,
        offset: toolbar.start,
        color,
        note: "",
      });
      onChange?.((prev) => [...prev, data]);
      dismissToolbar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that highlight");
    }
    setSaving(false);
  };

  const closeCapture = () => {
    if (capture?.blobUrl) URL.revokeObjectURL(capture.blobUrl);
    setCapture(null);
  };

  // A clickable link for the share sheet. The card itself carries the wordmark and site
  // name; a URL burned into a raster image can't be clicked, so tracking belongs here.
  const trackedLink = async () => {
    const home = window.location.origin;
    const fallback = withUtm(home, { source: "share_card", medium: "image", campaign: "card_share" });
    try {
      const { data } = await api.post(`/links/share-links`, { url: home, campaign: "card_share" });
      return data?.copy || fallback;
    } catch {
      return fallback;
    }
  };

  // Render the selection as a branded card. This draws from the selected TEXT server-side
  // rather than screenshotting the page: html2canvas cropped by viewport coordinates, which
  // silently pointed at the wrong content whenever the surrounding pane scrolled instead of
  // the window (as it does in the desktop study and the Go Deeper transcript).
  const captureAsImage = async () => {
    const sel = toolbar;
    if (!sel) return;
    setCapturing(true);
    const loadingToast = toast.loading("Making your card…");
    try {
      const { data: blob } = await api.post(
        "/cards/render",
        { kind: cardKind, text: sel.cardText, reference, translation },
        { responseType: "blob" }
      );
      setToolbar(null);
      window.getSelection()?.removeAllRanges();
      toast.dismiss(loadingToast);
      setCapture({ blobUrl: URL.createObjectURL(blob), blob });
    } catch {
      toast.dismiss(loadingToast);
      toast.error("Couldn't make that into an image");
    }
    setCapturing(false);
  };

  const downloadCapture = () => {
    if (!capture) return;
    const a = document.createElement("a");
    a.href = capture.blobUrl;
    a.download = "openword-highlight.png";
    a.click();
  };

  const copyCapture = async () => {
    if (!capture?.blob) return;
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": capture.blob })]);
      toast.success("Image copied");
    } catch {
      toast.error("Your browser doesn't support copying images — try Download instead");
    }
  };

  const shareCaptureNative = async () => {
    try {
      const file = new File([capture.blob], "openword-highlight.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "OpenWord AI", text: await trackedLink() });
      } else {
        toast.error("Native sharing isn't available here — try Download instead");
      }
    } catch { /* user cancelled the share sheet — not an error */ }
  };

  const openNote = (h, rect) => {
    setActiveId(h.id);
    setActiveRect(rect);
    setNoteDraft(h.note || "");
    setHoverId(null);
    window.dispatchEvent(new CustomEvent(NOTE_EVENT, { detail: instanceId }));
  };

  const saveNote = async () => {
    if (!activeHighlight) return;
    setSaving(true);
    try {
      await api.put(`/study/sessions/${sessionId}/highlights/${activeHighlight.id}`, { note: noteDraft.trim() });
      onChange?.((prev) => prev.map((h) => (h.id === activeHighlight.id ? { ...h, note: noteDraft.trim() } : h)));
      setActiveId(null);
    } catch {
      toast.error("Couldn't save the note");
    }
    setSaving(false);
  };

  const deleteHighlight = async () => {
    if (!activeHighlight) return;
    setSaving(true);
    try {
      await api.delete(`/study/sessions/${sessionId}/highlights/${activeHighlight.id}`);
      onChange?.((prev) => prev.filter((h) => h.id !== activeHighlight.id));
      setActiveId(null);
    } catch {
      toast.error("Couldn't remove the highlight");
    }
    setSaving(false);
  };

  const rt = (t) => (renderText ? renderText(t) : t);

  const segments = [];
  let cursor = 0;
  resolved.forEach((h) => {
    if (h.start > cursor) segments.push({ type: "text", text: plain.slice(cursor, h.start), key: `t-${cursor}` });
    segments.push({ type: "highlight", h, key: `h-${h.id}` });
    cursor = h.end;
  });
  if (cursor < plain.length || segments.length === 0) segments.push({ type: "text", text: plain.slice(cursor), key: "t-end" });

  const Tag = tag;

  return (
    <Tag ref={containerRef} className={className} data-testid={`highlightable-${block}`}>
      {segments.map((seg) => {
        if (seg.type === "text") return <span key={seg.key}>{rt(seg.text)}</span>;
        const h = seg.h;
        const c = HL_COLORS[h.color] || HL_COLORS.yellow;
        const hasNote = !!(h.note && h.note.trim());
        return (
          <mark
            key={seg.key}
            data-testid={`highlight-mark-${h.id}`}
            className={`hl-mark${hasNote ? " has-note" : ""}`}
            style={{
              backgroundColor: hoverId === h.id || activeId === h.id ? c.strong : c.mark,
              color: hasNote ? c.ring : "#18181B",
            }}
            onMouseEnter={(e) => {
              setHoverId(h.id);
              setHoverRect(e.currentTarget.getBoundingClientRect());
            }}
            onMouseLeave={() => setHoverId(null)}
            onClick={(e) => {
              e.stopPropagation();
              openNote(h, e.currentTarget.getBoundingClientRect());
            }}
          >
            {rt(plain.slice(h.start, h.end))}
          </mark>
        );
      })}

      {/* Hover preview — quick, read-only peek at the note, in a handwritten style */}
      {hoverId && hoverId !== activeId &&
        (() => {
          const h = resolved.find((x) => x.id === hoverId);
          if (!h || !(h.note && h.note.trim())) return null;
          const c = HL_COLORS[h.color] || HL_COLORS.yellow;
          return createPortal(
            <div
              data-testid={`highlight-preview-${h.id}`}
              className="fixed z-[70] pointer-events-none max-w-[16rem] rounded-lg px-3 py-2 shadow-xl font-handwriting text-lg leading-snug text-slate-200"
              style={{
                left: Math.min(hoverRect.left, window.innerWidth - 260),
                top: hoverRect.bottom + 8,
                backgroundColor: "#0E131F",
                border: `1px solid ${c.ring}55`,
              }}
            >
              {h.note}
            </div>,
            document.body
          );
        })()}

      {/* Selection toolbar — pick a highlight color */}
      {toolbar &&
        createPortal(
          <div
            ref={toolbarRef}
            data-testid={`highlight-toolbar-${block}`}
            className="fixed z-[80] -translate-x-1/2 -translate-y-full flex items-center gap-1.5 bg-[#07090E] rounded-full shadow-2xl px-2.5 py-2 animate-in fade-in zoom-in-95 duration-150"
            style={{ left: toolbar.x, top: toolbar.y - 10 }}
          >
            {Object.entries(HL_COLORS).map(([name, c]) => (
              <button
                key={name}
                type="button"
                data-testid={`highlight-color-${name}`}
                disabled={saving}
                onClick={() => createHighlight(name)}
                title={`Highlight ${name}`}
                className="w-6 h-6 rounded-full ring-2 ring-white/25 hover:scale-115 hover:ring-white/60 transition-all disabled:opacity-50"
                style={{ backgroundColor: c.mark }}
              />
            ))}
            {sharePath && IMAGE_CAPTURE_ENABLED && (
              <>
                <span className="w-px h-4 bg-[#0E131F]/15 mx-0.5" />
                <button
                  type="button"
                  data-testid="highlight-toolbar-capture"
                  disabled={saving || capturing}
                  onClick={captureAsImage}
                  title="Share this as an image"
                  className="w-6 h-6 flex items-center justify-center rounded-full text-slate-500 hover:text-white transition-colors disabled:opacity-50"
                >
                  {capturing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" strokeWidth={1.9} />}
                </button>
              </>
            )}
            <span className="w-px h-4 bg-[#0E131F]/15 mx-0.5" />
            <button
              type="button"
              data-testid="highlight-toolbar-cancel"
              onClick={dismissToolbar}
              className="w-6 h-6 flex items-center justify-center rounded-full text-slate-500 hover:text-white transition-colors"
            >
              <X className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          </div>,
          document.body
        )}

      {/* Note editor — click a highlight to add/edit/remove its note */}
      {activeHighlight &&
        createPortal(
          <div
            className="fixed inset-0 z-[80] bg-black/10"
            onClick={() => setActiveId(null)}
            data-testid="highlight-note-backdrop"
          >
            <div
              data-testid="highlight-note-editor"
              onClick={(e) => e.stopPropagation()}
              className="fixed w-[calc(100vw-2.5rem)] max-w-sm rounded-2xl shadow-2xl p-4 space-y-3 border"
              style={{
                backgroundColor: "#0E131F",
                borderColor: `${(HL_COLORS[activeHighlight.color] || HL_COLORS.yellow).ring}33`,
                left: Math.min(Math.max(12, activeRect?.left || 0), window.innerWidth - 380),
                top: Math.min((activeRect?.bottom || 0) + 10, window.innerHeight - 220),
              }}
            >
              <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">
                <PenLine className="w-3.5 h-3.5" strokeWidth={1.75} /> {editable ? "Your note" : "Note"}
              </div>
              <p className="font-handwriting text-lg text-slate-400 leading-snug italic line-clamp-2">
                &ldquo;{activeHighlight.text}&rdquo;
              </p>
              {editable ? (
                <>
                  <textarea
                    ref={noteInputRef}
                    data-testid="highlight-note-input"
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Write a note in the margin…"
                    rows={3}
                    maxLength={2000}
                    className="w-full rounded-xl border border-[#29344D] px-3 py-2 text-lg font-handwriting text-slate-200 leading-snug focus:outline-none focus:border-[#00E5FF]/50 resize-none bg-[#0E131F]"
                  />
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <button
                      type="button"
                      data-testid="highlight-note-delete"
                      onClick={deleteHighlight}
                      disabled={saving}
                      className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-500 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Remove highlight
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-testid="highlight-note-cancel"
                        onClick={() => setActiveId(null)}
                        className="text-sm text-slate-400 hover:text-slate-300 px-2"
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        data-testid="highlight-note-save"
                        onClick={saveNote}
                        disabled={saving}
                        className="rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium px-4 py-1.5 transition-colors disabled:opacity-50"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                activeHighlight.note && (
                  <p className="font-handwriting text-xl text-slate-200 leading-snug whitespace-pre-wrap">
                    {activeHighlight.note}
                  </p>
                )
              )}
            </div>
          </div>,
          document.body
        )}

      {/* Capture preview — the exact screenshot, ready to download/copy/share */}
      {capture &&
        createPortal(
          <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={closeCapture} data-testid="capture-backdrop">
            <div
              onClick={(e) => e.stopPropagation()}
              data-testid="capture-preview"
              className="bg-[#0E131F] rounded-2xl shadow-2xl p-4 space-y-4 max-w-sm w-full"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-widest text-slate-500">Share this image</span>
                <button data-testid="capture-close" onClick={closeCapture} className="text-slate-500 hover:text-slate-300">
                  <X className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </div>
              <img src={capture.blobUrl} alt="Highlighted excerpt" className="w-full rounded-xl border border-[#1E273D]" />
              <div className="grid grid-cols-3 gap-2">
                <button
                  data-testid="capture-download"
                  onClick={downloadCapture}
                  className="flex flex-col items-center gap-1 rounded-xl border border-[#29344D] py-2.5 text-xs text-slate-400 hover:bg-[#0B0F16] transition-colors"
                >
                  <Download className="w-4 h-4" strokeWidth={1.75} /> Download
                </button>
                <button
                  data-testid="capture-copy"
                  onClick={copyCapture}
                  className="flex flex-col items-center gap-1 rounded-xl border border-[#29344D] py-2.5 text-xs text-slate-400 hover:bg-[#0B0F16] transition-colors"
                >
                  <Copy className="w-4 h-4" strokeWidth={1.75} /> Copy
                </button>
                <button
                  data-testid="capture-share"
                  onClick={shareCaptureNative}
                  className="flex flex-col items-center gap-1 rounded-xl border border-[#29344D] py-2.5 text-xs text-slate-400 hover:bg-[#0B0F16] transition-colors"
                >
                  <Share2 className="w-4 h-4" strokeWidth={1.75} /> Share
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </Tag>
  );
}
